<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'

interface TourStep {
  target: string
  title: string
  description: string
  position: 'top' | 'bottom' | 'left' | 'right'
  route?: string
}

const props = defineProps<{
  autoStart?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const router = useRouter()
const route = useRoute()

const TOUR_KEY = 'tour_completed'
const TOUR_VERSION_KEY = 'tour_version'
const TOUR_VERSION = '1'

const isActive = ref(false)
const currentStep = ref(0)
const tooltipStyle = ref<Record<string, string>>({})
const targetRect = ref<DOMRect | null>(null)

const steps: TourStep[] = [
  {
    target: '[data-tour="welcome"]',
    title: 'Добро пожаловать!',
    description: 'Ассистент — интеллектуальный помощник для МИС-отдела. Отвечает на вопросы по документации и регламентам.',
    position: 'right',
  },
  {
    target: '[data-tour="chat-input"]',
    title: 'Задайте вопрос',
    description: 'Напишите вопрос, например: «Как зарегистрировать пациента?» Ассистент найдёт ответ в базе знаний.',
    position: 'top',
  },
  {
    target: '[data-tour="chat-messages"]',
    title: 'Ответы с источниками',
    description: 'Каждый ответ сопровождается ссылками на документы. Можно оценить ответ 👍 или 👎.',
    position: 'left',
  },
  {
    target: '[data-tour="sidebar-nav"]',
    title: 'Навигация',
    description: 'Переключайтесь между разделами системы. Здесь доступен чат с ассистентом.',
    position: 'right',
  },
]

const currentStepData = computed(() => steps[currentStep.value])
const isFirstStep = computed(() => currentStep.value === 0)
const isLastStep = computed(() => currentStep.value === steps.length - 1)
const progress = computed(() => `${currentStep.value + 1} / ${steps.length}`)

const start = () => {
  isActive.value = true
  currentStep.value = 0
  // Navigate to chat page first if not already there
  if (route.path !== '/') {
    router.push('/')
  }
  nextTick(() => focusStep(0))
}

const stop = () => {
  isActive.value = false
  localStorage.setItem(TOUR_KEY, 'true')
  localStorage.setItem(TOUR_VERSION_KEY, TOUR_VERSION)
  emit('close')
}

const focusStep = async (index: number) => {
  const step = steps[index]
  if (!step) return

  await nextTick()

  const el = document.querySelector(step.target) as HTMLElement
  if (!el) return

  const rect = el.getBoundingClientRect()
  targetRect.value = rect

  // Position tooltip relative to target
  const gap = 12
  const styles: Record<string, string> = {}

  switch (step.position) {
    case 'top':
      styles.top = `${rect.top - gap}px`
      styles.left = `${rect.left + rect.width / 2}px`
      styles.transform = 'translate(-50%, -100%)'
      break
    case 'bottom':
      styles.top = `${rect.bottom + gap}px`
      styles.left = `${rect.left + rect.width / 2}px`
      styles.transform = 'translate(-50%, 0)'
      break
    case 'left':
      styles.top = `${rect.top + rect.height / 2}px`
      styles.left = `${rect.left - gap}px`
      styles.transform = 'translate(-100%, -50%)'
      break
    case 'right':
      styles.top = `${rect.top + rect.height / 2}px`
      styles.left = `${rect.right + gap}px`
      styles.transform = 'translate(0, -50%)'
      break
  }

  // Keep tooltip within viewport bounds
  const vw = window.innerWidth
  const vh = window.innerHeight
  const left = parseFloat(styles.left)
  const top = parseFloat(styles.top)

  if (left < 10) styles.left = '16px'
  if (left > vw - 10) styles.left = `${vw - 16}px`
  if (top < 10) styles.top = '16px'
  if (top > vh - 10) styles.top = `${vh - 16}px`

  tooltipStyle.value = styles
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

watch(currentStep, (idx) => focusStep(idx))

const next = () => {
  if (currentStep.value < steps.length - 1) {
    currentStep.value++
  } else {
    stop()
  }
}

const prev = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

const handleKeydown = (e: KeyboardEvent) => {
  if (!isActive.value) return
  if (e.key === 'Escape') stop()
  if (e.key === 'ArrowRight' || e.key === 'Enter') next()
  if (e.key === 'ArrowLeft') prev()
}

const handleResize = () => {
  if (isActive.value) focusStep(currentStep.value)
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', handleResize)

  if (props.autoStart) {
    const completed = localStorage.getItem(TOUR_KEY)
    const version = localStorage.getItem(TOUR_VERSION_KEY)
    if (!completed || version !== TOUR_VERSION) {
      start()
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', handleResize)
})

// Expose start for parent
defineExpose({ start, stop })
</script>

<template>
  <Teleport to="body">
    <Transition name="tour-fade">
      <div v-if="isActive" class="tour-overlay">
        <!-- Backdrop -->
        <div class="tour-backdrop" @click.stop />

        <!-- Target highlight ring -->
        <div
          v-if="targetRect"
          class="tour-highlight"
          :style="{
            top: `${targetRect.top - 4}px`,
            left: `${targetRect.left - 4}px`,
            width: `${targetRect.width + 8}px`,
            height: `${targetRect.height + 8}px`,
          }"
        />

        <!-- Tooltip -->
        <div
          class="tour-tooltip"
          :style="tooltipStyle"
          @click.stop
        >
          <!-- Arrow -->
          <div
            class="tour-arrow"
            :class="`tour-arrow--${currentStepData.position}`"
          />

          <!-- Header -->
          <div class="tour-header">
            <span class="tour-progress">{{ progress }}</span>
          </div>

          <!-- Content -->
          <div class="tour-body">
            <h3 class="tour-title">{{ currentStepData.title }}</h3>
            <p class="tour-desc">{{ currentStepData.description }}</p>
          </div>

          <!-- Footer -->
          <div class="tour-footer">
            <button class="tour-btn tour-btn--ghost" @click="stop">
              Остановить
            </button>
            <div class="tour-footer-right">
              <button
                v-if="!isFirstStep"
                class="tour-btn tour-btn--outline"
                @click="prev"
              >
                ← Назад
              </button>
              <button
                class="tour-btn tour-btn--primary"
                @click="next"
              >
                {{ isLastStep ? 'Готово' : 'Далее →' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.tour-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.tour-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 0;
}

.tour-highlight {
  position: fixed;
  z-index: 1;
  border-radius: 8px;
  box-shadow: 0 0 0 4px var(--color-primary), 0 0 0 9999px rgba(0, 0, 0, 0.35);
  pointer-events: none;
  transition: all 0.3s ease;
}

.tour-tooltip {
  position: fixed;
  z-index: 2;
  width: 340px;
  max-width: 90vw;
  border-radius: 12px;
  background: var(--bg-elevated, #1e293b);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.08));
  overflow: hidden;
  transition: all 0.3s ease;
}

.tour-arrow {
  position: absolute;
  width: 12px;
  height: 12px;
  background: var(--bg-elevated, #1e293b);
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.08));
  transform: rotate(45deg);
}

.tour-arrow--top {
  bottom: -6px;
  left: 50%;
  margin-left: -6px;
  border-top: none;
  border-left: none;
}
.tour-arrow--bottom {
  top: -6px;
  left: 50%;
  margin-left: -6px;
  border-bottom: none;
  border-right: none;
}
.tour-arrow--left {
  right: -6px;
  top: 50%;
  margin-top: -6px;
  border-right: none;
  border-bottom: none;
}
.tour-arrow--right {
  left: -6px;
  top: 50%;
  margin-top: -6px;
  border-left: none;
  border-top: none;
}

.tour-header {
  padding: 12px 16px 0;
  display: flex;
  justify-content: flex-end;
}

.tour-progress {
  font-size: 0.75rem;
  color: var(--color-text-muted, #94a3b8);
  font-weight: 500;
}

.tour-body {
  padding: 8px 16px 12px;
}

.tour-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-heading, #f1f5f9);
  margin-bottom: 6px;
}

.tour-desc {
  font-size: 0.875rem;
  color: var(--color-text-secondary, #94a3b8);
  line-height: 1.5;
}

.tour-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid var(--glass-border, rgba(255, 255, 255, 0.06));
}

.tour-footer-right {
  display: flex;
  gap: 8px;
}

.tour-btn {
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
}

.tour-btn--primary {
  background: var(--color-primary, #25a8ed);
  color: #fff;
}
.tour-btn--primary:hover {
  filter: brightness(1.1);
}

.tour-btn--outline {
  background: transparent;
  color: var(--color-text-secondary, #94a3b8);
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.1));
}
.tour-btn--outline:hover {
  background: var(--glass-bg-hover, rgba(255, 255, 255, 0.05));
}

.tour-btn--ghost {
  background: transparent;
  color: var(--color-text-muted, #64748b);
}
.tour-btn--ghost:hover {
  color: var(--color-text, #e2e8f0);
}

.tour-fade-enter-active,
.tour-fade-leave-active {
  transition: opacity 0.25s ease;
}
.tour-fade-enter-from,
.tour-fade-leave-to {
  opacity: 0;
}
</style>
